import { getGTMContainerId } from '@dvag/dfs-constant-config/gtm';
import { addInitListener, getContext, getToken } from '@dvag/dfs-orchestrator-client';
import { defineCustomElements as designSystemDefineElementsNew } from '@dvag/design-system/loader';
import { defineCustomElements as defineDesignSystemIcons } from '@dvag/design-system-icons/loader';
import { StrictMode } from 'react';
import ReactDOM, { Root } from 'react-dom/client';

import { getLocale } from 'locale/i18n';
import { App } from 'App';
import { getDevEnv } from './service/envConfig';
import { appConfigurationInit, getEnvironmentApiUrls } from '@dvag/dfs-constant-config';
import { initMonitoringSdk } from '@dvag/dfs-monitoring-sdk-fe';
import featureFlag from './service/featureFlag';

const htmlEl = document.documentElement || document.getElementsByTagName('html')[0];
htmlEl.lang = getLocale();

interface WindowExtended extends Window {
  dataLayer?: { ['gtm.start']: number; event: string }[];
}
// Google Tag Manager
((w: WindowExtended, d, s, l: 'dataLayer', i) => {
  const w1 = w;
  w1[l] = w1[l] || [];
  w1[l]?.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const f = d.getElementsByTagName(s)[0];
  const j: Node & { async: boolean; src: string } = d.createElement(s) as HTMLElement & {
    async: boolean;
    src: string;
  };
  j.async = true;
  j.src = `https://www.googletagmanager.com/gtm.js?id=${i}`;
  if (f.parentNode) {
    f.parentNode.insertBefore(j, f);
  }
})(window, document, 'script', 'dataLayer', getGTMContainerId(getDevEnv()));
// End Google Tag Manager

const USERLANE_PROJECT_ID = 'vywl2';
const USERLANE_SCRIPT_SRC = 'https://cdn.userlane.com/userlane.js';

type UserlaneCommand = ((command: string, ...args: unknown[]) => void) & {
  q?: unknown[];
};

interface UserlaneWindow extends Window {
  Userlane?: UserlaneCommand;
  UserlaneCommandObject?: string;
}

const loadUserlane = () => {
  const userlaneWindow = globalThis as unknown as UserlaneWindow;
  userlaneWindow.UserlaneCommandObject = 'Userlane';
  if (!userlaneWindow.Userlane) {
    const queued: UserlaneCommand = (...args: unknown[]) => {
      queued.q ??= [];
      queued.q.push(args);
    };
    userlaneWindow.Userlane = queued;
  }
  if (document.querySelector(`script[src="${USERLANE_SCRIPT_SRC}"]`)) {
    return;
  }
  const script = document.createElement('script');
  const firstScript = document.getElementsByTagName('script')[0];
  script.async = true;
  script.src = USERLANE_SCRIPT_SRC;
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  }
};

const initUserlane = () => {
  const { Userlane } = globalThis as unknown as UserlaneWindow;
  const advisorId = getContext().user?.advisor?.id;
  if (Userlane && advisorId) {
    Userlane('identify', advisorId);
    Userlane('init', USERLANE_PROJECT_ID);
  }
};

loadUserlane();

let root: Root | undefined;

export const initializeApp = () =>
  appConfigurationInit(() => getToken() ?? '').finally(() => {
    const rootElement = document.getElementById('root');
    if (!rootElement) {
      throw new Error('Failed to find the root element');
    }

    const apiUrls = getEnvironmentApiUrls();

    const { user } = getContext();
    const { id: advisorId } = user?.advisor ?? {};

    const requestTracingConfig = {
      propagateTraceHeaderCorsUrls: [apiUrls.advisor, apiUrls.person].map(
        (it) => new RegExp(new URL(it).hostname),
      ),
      ignoreUrls: [
        new RegExp(new URL(apiUrls.otelCollector.endpoint).hostname),
        /applicationinsights\.azure\.com/,
      ],
    };

    initMonitoringSdk({
      serviceName: 'dfs-vp-digital.dfs-person-fe.APP-00122',
      collectorEndpoint: apiUrls.otelCollector.endpoint,
      instrumentationKey: apiUrls.otelCollector.instrumentationKey,
      userId: advisorId,
      autoInstrumentations: {
        '@opentelemetry/instrumentation-fetch': requestTracingConfig,
        '@opentelemetry/instrumentation-xml-http-request': requestTracingConfig,
      },
    });

    featureFlag.init();

    initUserlane();

    if (!root) {
      root = ReactDOM.createRoot(rootElement);
      designSystemDefineElementsNew();
      defineDesignSystemIcons();
    }

    root.render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  });

addInitListener(initializeApp);
